<script setup lang="ts">
import { ref, watch } from 'vue'

import BaseCard from '@tix/ds/components/atoms/BaseCard.vue'
import FormInput from '@tix/ds/components/atoms/FormInput.vue'
import type { FormInputExposed } from '@tix/ds/components/atoms/form-input/types.ts'

import { type DonationOptionsPresentation, type DonationOptionsValues } from '@/helpers/donationSurvey'

type Props = {
  title?: string
  presentation: DonationOptionsPresentation
}

export type DonationOptionsExposed = {
  validateAndReturnError: () => string | null
}

const model = defineModel<DonationOptionsValues>({ required: true })

const props = withDefaults(defineProps<Props>(), {
  title: 'Donation options',
})

watch(
  () => model.value.dedicate,
  (value) => {
    if (!value) {
      model.value.honoreeFirstName = ''
      model.value.honoreeLastName = ''
    }
  },
)

const honoreeFirstNameRef = ref<FormInputExposed | null>(null)
const honoreeLastNameRef = ref<FormInputExposed | null>(null)

function validateAndReturnError(): string | null {
  if (!model.value.dedicate) return null
  let countOfErrors = 0

  if (honoreeFirstNameRef.value?.validateAndReturnError()) countOfErrors++
  if (honoreeLastNameRef.value?.validateAndReturnError()) countOfErrors++

  if (countOfErrors === 0) return null

  return countOfErrors === 1 ? `1 field needs your attention.` : `${countOfErrors} fields need your attention.`
}

defineExpose({ validateAndReturnError } satisfies DonationOptionsExposed)
</script>

<template>
  <div class="donation-options">
    <h2 v-if="title">{{ props.title }}</h2>

    <template v-if="presentation.dedication">
      <BaseCard :class="{ checked: model.dedicate }" padding="comfy">
        <FormInput
          id="donation-dedication"
          v-model="model.dedicate"
          :description="presentation.dedication.checkbox.description"
          :label="presentation.dedication.checkbox.label"
          type="checkbox"
        />
      </BaseCard>

      <div v-if="model.dedicate" class="honoree-name-inputs">
        <FormInput
          id="honoree-first-name"
          ref="honoreeFirstNameRef"
          v-model="model.honoreeFirstName"
          class="honoree-name-input"
          :label="presentation.dedication.honoreeFirstName.label"
          required
          type="string"
        />
        <FormInput
          id="honoree-last-name"
          ref="honoreeLastNameRef"
          v-model="model.honoreeLastName"
          class="honoree-name-input"
          :label="presentation.dedication.honoreeLastName.label"
          required
          type="string"
        />
      </div>
    </template>

    <BaseCard v-if="presentation.anonymous" :class="{ checked: model.anonymous }" padding="comfy">
      <FormInput
        id="donation-anonymity"
        v-model="model.anonymous"
        :description="presentation.anonymous.description"
        :label="presentation.anonymous.label"
        type="checkbox"
      />
    </BaseCard>
  </div>
</template>

<style scoped>
.donation-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

h2 {
  font-size: var(--font-size-h3);
}

.base-card {
  &.checked {
    background-color: var(--action-card-bg-active, #001d3e);
    border-color: var(--action-card-border-active, #0074c7);
  }
}

.form-input.checkbox {
  & :deep(label) {
    font-weight: var(--form-label-font-weight);
  }

  & :deep(.description) {
    position: relative;
    /* TODO: Fix this in FormInput and support different input sizes */
    left: -35px;
    margin-top: var(--space-0-5);
  }
}

.honoree-name-inputs {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5);
  padding: 0 var(--space-1-5);
  margin-bottom: var(--padding-tight);
}
</style>
